import { useEffect, useMemo, useRef, useState } from "react";
import ReactSelect from "react-select";
import { Clock } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import MyDialogContent from "@/components/MyDialogContent";
import CustomDatepicker from "@/components/customeDatepicker";
import {
  getPatientContextTimezoneDisplay,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import type { NursingNoteV2, PatientV2 } from "../types";
import {
  createDefaultNursingNoteForm,
  NURSING_CARE_TYPE_OPTIONS,
  NURSING_NOTE_TYPE_OPTIONS,
  NURSING_PROGRAM_OPTIONS,
  type NursingNoteFormState,
} from "./nursingNoteConstants";
import {
  getPatientTimeZone,
  isNursingNoteDateBeyondPatientNow,
  isNursingNoteDateTimeInFutureForPatient,
  joinNursingNoteDateTime,
  nursingNoteFormDateTimeToUtcIso,
  splitNursingNoteDateTime,
  toNursingNoteFormDateTime,
} from "./nursingNoteHelpers";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: PatientV2 | null;
  patientId: string;
  mode: "add" | "edit" | "view";
  note?: NursingNoteV2 | null;
  authorName: string;
  onSaved?: (note: NursingNoteV2, isEdit: boolean) => void;
};

export function NursingNoteFormDialog({
  open,
  onOpenChange,
  patient,
  patientId,
  mode,
  note,
  authorName,
  onSaved,
}: Props) {
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const patientTimeZone = useMemo(() => getPatientTimeZone(patient), [patient]);
  const [form, setForm] = useState<NursingNoteFormState>(() =>
    createDefaultNursingNoteForm(patientTimeZone),
  );
  const [saving, setSaving] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setForm(createDefaultNursingNoteForm(patientTimeZone));
      return;
    }
    if (mode === "add") {
      setForm(createDefaultNursingNoteForm(patientTimeZone));
      return;
    }
    if (note) {
      const programValue = note?.program ? String(note.program).toLowerCase().trim() : "";
      const validProgram =
        NURSING_PROGRAM_OPTIONS.find((opt) => opt.value === programValue)?.value ||
        programValue ||
        "";
      setForm({
        program: validProgram,
        careType: note?.careType
          ? Array.isArray(note.careType)
            ? note.careType
            : String(note.careType).split(",")
          : [],
        dateTime: toNursingNoteFormDateTime(note as Record<string, unknown>),
        duration: note?.duration != null ? String(note.duration) : "",
        notes: String((note as Record<string, unknown>)?.content ?? ""),
        note_type: String((note as Record<string, unknown>)?.note_type ?? ""),
      });
    }
  }, [open, mode, note, patientTimeZone]);

  useEffect(() => {
    if (open && mode === "add") {
      setForm((prev) => ({
        ...prev,
        dateTime: createDefaultNursingNoteForm(patientTimeZone).dateTime,
      }));
    }
  }, [open, mode, patientTimeZone]);

  const openTimePicker = () => {
    if (isView) return;
    const el = timeInputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        void el.showPicker();
      } else {
        el.focus();
        el.click();
      }
    } catch {
      el.focus();
    }
  };

  const heading = isView
    ? "View Nursing Note (Manual Time)"
    : isEdit
      ? "Edit Nursing Note (Manual Time)"
      : "Add Nursing Note (Manual Time)";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isView) return;

    const { program, careType, dateTime, duration, notes, note_type } = form;
    const durationInMinutes = Number(duration);

    if (!program || !careType.length || !dateTime || !duration || !note_type) {
      toast.error("Please fill out all required fields");
      return;
    }
    if (!notes.trim()) {
      toast.error("Please enter note content");
      return;
    }
    if (!Number.isFinite(durationInMinutes) || durationInMinutes <= 0) {
      toast.error("Duration must be a positive number");
      return;
    }

    const { dateYmd } = splitNursingNoteDateTime(dateTime);
    if (isNursingNoteDateBeyondPatientNow(dateYmd, patientTimeZone)) {
      toast.error("Date and time cannot be in the future in the patient's timezone.");
      return;
    }
    if (isNursingNoteDateTimeInFutureForPatient(dateTime, patientTimeZone)) {
      toast.error("Date and time cannot be in the future in the patient's timezone.");
      return;
    }

    const utcIsoForSave =
      nursingNoteFormDateTimeToUtcIso(dateTime, patientTimeZone) ?? new Date().toISOString();
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setSaving(true);
      const payload = {
        content: notes.trim(),
        authorName,
        patientId: isEdit ? patientId : [patientId],
        program,
        careType: careType.join(","),
        dateTime: utcIsoForSave,
        duration: durationInMinutes,
        note_type,
      };

      if (isEdit && note?._id) {
        const { data } = await AxiosInstance().put(`/patients/note/${note._id}`, {
          ...payload,
          patientId,
        });
        const updated = (data as { data?: NursingNoteV2 })?.data;
        if (!updated) throw new Error("Update failed");
        onSaved?.(updated, true);
        onOpenChange(false);
        resolveFn("Nursing note updated successfully");
        return;
      }

      const { data } = await AxiosInstance().post("/patients/nursing-note", payload);
      const created = (data as { data?: NursingNoteV2 })?.data;
      if (!created) throw new Error("Create failed");
      onSaved?.(created, false);
      onOpenChange(false);
      resolveFn("Nursing note added successfully");
    } catch (error) {
      rejectFn(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent heading={heading}>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nursing-program" className="flex items-center gap-1">
                Select Program <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.program || undefined}
                onValueChange={(value) => setForm((prev) => ({ ...prev, program: value }))}
                disabled={isView}
              >
                <SelectTrigger id="nursing-program" className="w-full">
                  <SelectValue placeholder="Select Program" />
                </SelectTrigger>
                <SelectContent>
                  {NURSING_PROGRAM_OPTIONS.filter((opt) => {
                    if (isEdit && form.program === opt.value) return true;
                    return patient?.programs?.join(",").includes(opt.value);
                  }).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nursing-care-type" className="flex items-center gap-1">
                Select Care <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                isMulti
                isDisabled={isView}
                value={NURSING_CARE_TYPE_OPTIONS.filter((opt) => form.careType.includes(opt.value))}
                onChange={(selectedOptions) =>
                  setForm((prev) => ({
                    ...prev,
                    careType: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  }))
                }
                options={NURSING_CARE_TYPE_OPTIONS}
                placeholder="Select Care Type"
                className="w-full text-sm"
                classNamePrefix="rs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nursing-note-type" className="flex items-center gap-1">
                Note Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.note_type || undefined}
                onValueChange={(value) => setForm((prev) => ({ ...prev, note_type: value }))}
                disabled={isView}
              >
                <SelectTrigger id="nursing-note-type" className="w-full">
                  <SelectValue placeholder="Select Note Type" />
                </SelectTrigger>
                <SelectContent>
                  {NURSING_NOTE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label className="flex items-center gap-1">
                Date and Time <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="nursing-date" className="text-xs text-muted-foreground">
                    Date
                  </Label>
                  <CustomDatepicker
                    id="nursing-date"
                    maxDate={new Date()}
                    value={splitNursingNoteDateTime(form.dateTime).dateYmd}
                    onChange={(ymd) => {
                      const { timeHm } = splitNursingNoteDateTime(form.dateTime);
                      const combined = joinNursingNoteDateTime(ymd, timeHm || "00:00");
                      if (
                        isNursingNoteDateBeyondPatientNow(ymd, patientTimeZone) ||
                        isNursingNoteDateTimeInFutureForPatient(combined, patientTimeZone)
                      ) {
                        toast.error(
                          "Date and time cannot be in the future in the patient's timezone.",
                        );
                      }
                      setForm((prev) => ({ ...prev, dateTime: combined }));
                    }}
                    disabled={isView}
                    placeholder="Select date"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="nursing-time" className="text-xs text-muted-foreground">
                    Time
                  </Label>
                  <div className="relative cursor-pointer" onClick={openTimePicker}>
                    <Input
                      ref={timeInputRef}
                      id="nursing-time"
                      type="time"
                      className="w-full pr-10 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:h-0 [&::-webkit-calendar-picker-indicator]:w-0 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:pointer-events-none"
                      disabled={isView}
                      value={splitNursingNoteDateTime(form.dateTime).timeHm}
                      onChange={(event) => {
                        const timeHm = event.target.value;
                        const { dateYmd } = splitNursingNoteDateTime(form.dateTime);
                        if (!dateYmd) {
                          toast.error("Please select a date first.");
                          return;
                        }
                        const combined = joinNursingNoteDateTime(dateYmd, timeHm);
                        if (isNursingNoteDateTimeInFutureForPatient(combined, patientTimeZone)) {
                          toast.error(
                            "Date and time cannot be in the future in the patient's timezone.",
                          );
                        }
                        setForm((prev) => ({ ...prev, dateTime: combined }));
                      }}
                      required
                    />
                    <Clock
                      className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden
                    />
                  </div>
                </div>
              </div>
              <div className="text-muted-foreground flex items-center gap-1 text-xs font-bold">
                <span>
                  Please select the time as per patient&apos;s timezone{" "}
                  <span className="font-medium text-green-600">
                    {getPatientContextTimezoneDisplay(patient)}
                  </span>
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nursing-duration" className="flex items-center gap-1">
                Duration (in Minutes) <span className="text-destructive">*</span>
              </Label>
              <Input
                disabled={isView}
                id="nursing-duration"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                placeholder="Enter duration"
                className="w-full"
                value={form.duration}
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-", "."].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(event) => {
                  const digitsOnly = event.target.value.replace(/[^\d]/g, "");
                  setForm((prev) => ({ ...prev, duration: digitsOnly }));
                }}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="nursing-note-content" className="flex items-center gap-1">
              Notes <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="nursing-note-content"
              disabled={isView}
              placeholder="Add nursing note..."
              rows={6}
              className="w-full"
              value={form.notes}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              required
            />
          </div>

          {!isView ? (
            <div className="sticky bottom-0 flex w-full justify-end gap-3 bg-white">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={saving} type="submit">
                {isEdit ? "Update" : "Save"}
              </Button>
            </div>
          ) : null}
        </form>
      </MyDialogContent>
    </Dialog>
  );
}
